import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api, { setToken } from "@/lib/api";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      setUser(res.data);
      return res.data;
    } catch (e) {
      setUser(null);
      return null;
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    const token = localStorage.getItem("fc_token");
    if (!token) {
      setLoading(false);
      return;
    }
    refresh().finally(() => setLoading(false));
  }, [refresh]);

  const login = async (email, password) => {
    const res = await api.post("/auth/login", { email, password });
    setToken(res.data.session_token);
    setUser(res.data.user);
    return res.data.user;
  };

  const register = async (name, email, password, attribution = {}) => {
    const payload = { name, email, password };
    if (attribution.ref) payload.ref = attribution.ref;
    if (attribution.invite) payload.invite = attribution.invite;
    const res = await api.post("/auth/register", payload);
    if (res.data?.session_token) {
      setToken(res.data.session_token);
      setUser(res.data.user);
      return res.data.user;
    }
    // Approval-gated: no session issued — awaiting admin approval.
    return { pending: true, message: res.data?.message };
  };

  const exchangeGoogle = async (sessionId) => {
    const payload = { session_id: sessionId };
    // Carry any pending referral attribution captured before the Google redirect.
    const ref = typeof window !== "undefined" ? localStorage.getItem("fc_pending_ref") : null;
    const invite = typeof window !== "undefined" ? localStorage.getItem("fc_pending_invite") : null;
    if (ref) payload.ref = ref;
    if (invite) payload.invite = invite;
    const res = await api.post("/auth/google", payload);
    if (typeof window !== "undefined") {
      localStorage.removeItem("fc_pending_ref");
      localStorage.removeItem("fc_pending_invite");
    }
    if (res.data?.session_token) {
      setToken(res.data.session_token);
      setUser(res.data.user);
      return res.data.user;
    }
    // Approval-gated: account exists but isn't active yet.
    return { pending: true, status: res.data?.status, message: res.data?.message };
  };

  const logout = async () => {
    try {
      await api.post("/auth/logout");
    } catch (e) {
      /* ignore */
    }
    setToken(null);
    setUser(null);
  };

  const capabilities = user?.capabilities || [];
  const can = (cap) => capabilities.includes(cap);
  const value = {
    user, setUser, loading, login, register, exchangeGoogle, logout, refresh,
    capabilities,
    can,
    roles: user?.roles || [],
    isOwner: !!user?.is_owner,
    isAdmin: capabilities.includes("manage_roles"),     // admin/owner
    isProducer: capabilities.includes("producer_tools"),
    isCore: capabilities.includes("view_members"),       // leadership council (or admin)
    isPodcastEligible: capabilities.includes("rsvp_podcast"),
  };
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
